// This is VolumeBarChart.js
// >>>>>>>>>>>>>>>>>>>>>>> Code to add >>>>>>>>
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";
import { PERIOD_CONFIG } from "../DateFilterButtons";
import "./VolumeBarChart.css";


// >>>>>>>>>>>>>>>>>>>>>>> Code to add >>>>>>>>
/** Format to YYYY-MM-DD (safe if already ISO). */

const formatISODate = (dateStr) => {
  if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getUTCDate = (dateStr) => {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getWeeklyBucketStartUTC = (date) => {
  const utcMidnight = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utcMidnight.getUTCDay();
  const daysFromMonday = (day + 6) % 7;
  utcMidnight.setUTCDate(utcMidnight.getUTCDate() - daysFromMonday);
  return utcMidnight;
};

const getBucketKey = (dateStr, granularity) => {
  const d = getUTCDate(dateStr);
  if (!d) return String(dateStr);

  if (granularity === "weekly") {
    return getWeeklyBucketStartUTC(d).toISOString().slice(0, 10);
  }
  if (granularity === "monthly") {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
  }
  if (granularity === "yearly") {
    return `${d.getUTCFullYear()}-01-01`;
  }
  return d.toISOString().slice(0, 10);
};

const aggregateVolumeData = (data, selectedPeriod) => {
  if (!data || !data.length) return data;

  const granularity =
    selectedPeriod === "MAX"
      ? "daily"
      : (PERIOD_CONFIG[selectedPeriod]?.candleGranularity || "daily");

  if (granularity === "daily") return data;

  const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const buckets = new Map();

  sorted.forEach((item) => {
    const bucketKey = getBucketKey(item.date, granularity);
    const existing = buckets.get(bucketKey);

    if (!existing) {
      buckets.set(bucketKey, {
        ...item,
        date: bucketKey,
        volume: Number(item.volume) || 0
      });
      return;
    }

    existing.volume += Number(item.volume) || 0;
    existing.close = item.close;
  });

  return Array.from(buckets.values());
};

const VolumeBarChart = ({ chartData, stockInfo, selectedPeriod }) => {
  if (!chartData || chartData.length === 0) {
    return <p>Loading chart data...</p>;
  }

  const aggregatedData = aggregateVolumeData(chartData, selectedPeriod);

  // Match VWAP logic: months for short ranges; years for 3Y/5Y/MAX
  const showYears = ["3Y", "5Y", "MAX"].includes(selectedPeriod);

  // One tick per year for long ranges
  const xTicks = showYears
    ? Array.from(new Set(aggregatedData.map((d) => new Date(d.date).getFullYear())))
        .map((year) => {
          const first = aggregatedData.find(
            (d) => new Date(d.date).getFullYear() === year
          );
          return first?.date;
        })
        .filter(Boolean)
    : undefined;

  // Keep raw date for axis, but precompute a nice label for tooltip
  const formattedData = aggregatedData.map((day) => ({
    ...day,
    formattedDate: new Date(day.date).toLocaleDateString("en-US", {
      // For parity with VWAP: month+day on short ranges; year on long ranges
      month: showYears ? undefined : "short",
      day: showYears ? undefined : "numeric",
      year: showYears ? "numeric" : undefined
    })
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="volume-bar-tooltip">
          <p className="date">{ formatISODate(data.date) }</p>
          <p className="value">
            Volume: <span>{data.volume.toLocaleString()}</span>
          </p>
          <p className="price">
            Close: <span>${data.close.toFixed(2)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (value) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
    return value;
  };

  return (
    <div className="chart-content">
      <h3>
        Trading Volume - {stockInfo.name} ({stockInfo.ticker})
      </h3>
      <div className="volume-bar-chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              ticks={xTicks}
              tickFormatter={(value) => {
                const d = new Date(value);
                return showYears
                  ? d.getFullYear().toString()
                  : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              }}
            />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} width={60} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="volume"
              name="Volume"
              className="volume-bar"
              fill="#4287f5"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VolumeBarChart;
